import { ReactNode, useState } from 'react';

import { CompositeDecorator, ContentState, EditorState, RichUtils } from 'draft-js';

import { ActionText } from '@components/action-text';
import { Button } from '@components/button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconTextFormatLink } from '@components/icons/text-format-link';
import { IconTrash } from '@components/icons/trash';
import { PopoverButton } from '@components/popover-button';
import { Stack } from '@components/stack';
import { TextField } from '@components/text-field';
import { Keys } from '@components/types/keyboard';
import { findEntitiesByType } from '@utils/draftjs';

import { useTextEditorContext } from './text-editor';

export function AnchorButton(textProps: AnchorButtonProps) {
  const { editorState, setEditorState } = useTextEditorContext();
  const [url, setUrl] = useState<string | null>(null);
  const selection = editorState.getSelection();
  const contentState = editorState.getCurrentContent();
  const startOffset = selection.getStartOffset();
  const endOffset = selection.getEndOffset();
  const anchorKey = selection.getAnchorKey();
  const currentContentBlock = contentState.getBlockForKey(anchorKey);
  const selectedText = currentContentBlock.getText().slice(startOffset, endOffset);
  const hasSelection = selectedText.length > 0;

  let currentUrl = '';
  if (!selection.isCollapsed()) {
    const startKey = selection.getStartKey();
    const blockWithLinkAtBeginning = contentState.getBlockForKey(startKey);
    const linkKey = blockWithLinkAtBeginning.getEntityAt(startOffset);
    if (linkKey) {
      const linkInstance = contentState.getEntity(linkKey);
      currentUrl = linkInstance.getData().url;
    }
  }
  const canRemove = currentUrl !== '';

  const confirmUrl = () => {
    if (!url) {
      return;
    }
    const hyperLink =
      url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
    const contentStateWithEntity = contentState.createEntity('LINK', 'MUTABLE', {
      url: hyperLink,
    });

    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const currentState = EditorState.set(editorState, {
      currentContent: contentStateWithEntity,
    });

    let newState = RichUtils.toggleLink(currentState, currentState.getSelection(), entityKey);
    newState = EditorState.forceSelection(newState, editorState.getSelection());
    if (newState) {
      setEditorState(newState);
    }
  };

  const removeUrl = () => {
    if (currentUrl !== '') {
      const newState = RichUtils.toggleLink(editorState, selection, null);
      setEditorState(newState);
    }
  };

  return (
    <PopoverButton
      placement="bottom-start"
      triggerProps={{
        ...(hasSelection ? {} : { tooltip: textProps.linkToolTip }),
        iconLeft: <IconTextFormatLink />,
        variant: 'ghost',
        isDisabled: !hasSelection,
        onClick: () => {
          setUrl(currentUrl);
        },
      }}
      contentStyle={{ width: '20rem' }}
    >
      {({ close }) => (
        <Stack space={4}>
          <TextField
            label={textProps.label}
            value={url === null ? currentUrl : url}
            onChange={setUrl}
            placeholder="https://"
            onKeyDown={(e) => {
              if (e.key === Keys.Enter) {
                e.preventDefault();
                confirmUrl();
                setUrl(null);
                close();
              }
            }}
          />
          <Stack space={2} orientation="horizontal" justify="flex-end">
            {canRemove && (
              <Button
                variant="secondary"
                iconLeft={<IconTrash />}
                onClick={() => {
                  removeUrl();
                  setUrl(null);
                  close();
                }}
              />
            )}
            <Button
              text={canRemove ? textProps.save : textProps.addLink}
              {...(canRemove ? {} : { iconLeft: <IconAddPlus /> })}
              variant="primary-blue"
              onClick={() => {
                confirmUrl();
                setUrl(null);
                close();
              }}
            />
          </Stack>
        </Stack>
      )}
    </PopoverButton>
  );
}

function Link(props: { contentState: ContentState; entityKey: string; children: ReactNode }) {
  const { url } = props.contentState.getEntity(props.entityKey).getData();
  return <ActionText.ExternalLink href={url}>{props.children}</ActionText.ExternalLink>;
}

export function createAnchorPlugin() {
  return {
    decorators: [
      new CompositeDecorator([
        {
          strategy: findEntitiesByType('LINK'),
          component: Link,
        },
      ]),
    ],
  };
}

export type AnchorButtonProps = {
  save: string;
  addLink: string;
  linkToolTip: string;
  label: string;
};
